import React, { Dispatch, SetStateAction, useContext, useEffect } from 'react';
import { Form, FormikProps } from 'formik';
import { useIntl } from 'react-intl';

import { useCurrentTheme } from 'app/shared/theme';
import { Ticket } from 'app/shared/typings';
import { AlertContext } from 'app/shared/context';
import { Divider } from 'app/shared/components/atoms';
import { Box } from 'app/shared/components/atoms';
import { ActionButton } from 'app/shared/components/molecules';

import { AttendeeDetailFormSection } from './AttendeeDetailFormSection';

interface Props extends FormikProps<any> {
  tickets: Ticket[];
  formDirty: boolean;
  setFormDirty: Dispatch<SetStateAction<boolean>>;
}

function ticketHasValues(ticket: Ticket) {
  if (ticket.firstName || ticket.lastName || ticket.email) {
    return true;
  }
  return false;
}

export const FormContent: React.FC<React.PropsWithChildren<Props>> = ({
  handleChange,
  isSubmitting,
  status,
  values,
  setStatus,
  errors,
  tickets,
  formDirty,
  setFormDirty,
}) => {
  const { colors } = useCurrentTheme();
  const { formatMessage } = useIntl();
  const alertContext = useContext(AlertContext);

  useEffect(() => {
    if (status?.success) {
      alertContext.addAlert({
        type: 'success',
        message: formatMessage({
          id: 'event.attendeeDetailsForm.success',
        }),
      });
    } else if (status?.error) {
      alertContext.addAlert({
        type: 'error',
        message: status.error,
      });
    } else {
      alertContext.clearAlerts();
    }
  }, [status]);

  return (
    <Form>
      <Box flexDirection="column" mt={5} mb={4} gap={8}>
        {tickets.map((ticket: Ticket, index: number) => (
          <Box w="100%" key={ticket.id}>
            {index !== 0 && (
              <Divider color={colors.accent.primaryAccent.base} mt={7} mb={6} />
            )}
            <AttendeeDetailFormSection
              key={ticket.id}
              index={index}
              values={values}
              errors={errors}
              status={status}
              initiallyDisabled={ticketHasValues(ticket)}
              setFormDirty={setFormDirty}
              handleChange={(e: any) => {
                setFormDirty(true);
                setStatus({ success: false, error: undefined });
                handleChange(e);
              }}
            />
          </Box>
        ))}
        <ActionButton
          type="submit"
          variant="primary"
          isLoading={isSubmitting}
          disabled={!formDirty}
          fullWidth={false}
        >
          {formatMessage({ id: 'event.attendeeDetailsForm.save' })}
        </ActionButton>
      </Box>
    </Form>
  );
};
